import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
  BLUR_COMMAND,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_ENTER_COMMAND,
  TextNode,
} from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { createPortal } from 'react-dom';
import {
  UserSuggestion,
  useUserSuggestions,
} from '../../common/use-user-suggestions';
import { checkForAtSignMentions } from '../mention-shared/at-sign-matcher';
import { $handleMentionBackspace, $insertMention } from './mention-commands';
import {
  isOpenProfileShortcut,
  MentionMenuItem,
  useOpenMentionProfile,
} from '../mention-shared/mention-menu-item';
import {
  useMentionComboboxAria,
  useMentionListboxId,
} from '../mention-shared/use-mention-combobox-aria';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

class MentionTypeAheadOption extends MenuOption {
  id: string;
  name: string;
  avatar: UserSuggestion['avatar'];
  displayAttributes: string[];

  constructor(suggestion: UserSuggestion) {
    super(suggestion.name);
    this.id = String(suggestion.id);
    this.name = suggestion.name;
    this.avatar = suggestion.avatar;
    this.displayAttributes = suggestion.display_attributes;
  }
}

// Mounted only while the menu is, so mount/unmount is the open/close signal.
// Lexical's typeahead plugin sets `aria-activedescendant` itself
// (`typeahead-item-<index>`); this adds the combobox role/expanded/controls it
// leaves out. The row ids in the menu give that reference a target.
const MentionListboxAria: React.FC<{ listboxId: string }> = ({ listboxId }) => {
  const [editor] = useLexicalComposerContext();
  useMentionComboboxAria(
    () => editor.getRootElement(),
    true,
    listboxId,
    undefined
  );
  return null;
};

const MentionPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string>('');
  const suggestions = useUserSuggestions(queryString);
  // `LexicalTypeaheadMenuPlugin` only ever closes its menu from an editor
  // update listener. Moving focus to another field (the UGC Title input, say)
  // is not an editor update, so the dropdown would otherwise stay open over a
  // field the user has already left. Hide it from blur until the plugin next
  // recomputes the trigger against a live selection — which is exactly when
  // `onQueryChange` fires, so the menu never reappears at a stale position.
  const [suppressedByBlur, setSuppressedByBlur] = useState(false);
  const listboxId = useMentionListboxId();
  const openProfile = useOpenMentionProfile();
  // The menu's highlighted row lives in the typeahead plugin's render
  // callback; mirror it here so the Alt+Enter command can read it.
  const highlightedUserId = useRef<string | null>(null);

  const handleQueryChange = useCallback((matchingString: string | null) => {
    setSuppressedByBlur(false);
    // Trim so multi-word queries search on their content (e.g. "Jo " -> "Jo")
    // rather than sending trailing/leading spaces to the user search.
    setQueryString(matchingString?.trim() ?? '');
  }, []);

  const users = useMemo(
    () => suggestions.map((s) => new MentionTypeAheadOption(s)),
    [suggestions]
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeAheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        $insertMention(
          nodeToReplace,
          selectedOption.id,
          selectedOption.name,
          'user'
        );
        closeMenu();
      });
    },
    [editor]
  );

  useEffect(
    () =>
      mergeRegister(
        // Backspacing the auto-inserted space right after a mention removes both
        // the space and the whole token (spec #18); returning true prevents the
        // default single-character delete.
        editor.registerCommand(
          KEY_BACKSPACE_COMMAND,
          () => $handleMentionBackspace(),
          COMMAND_PRIORITY_LOW
        ),
        // Alt+Enter opens the highlighted user's profile (the profile link is
        // not a Tab stop). Runs above the typeahead plugin's own Enter handler,
        // which would otherwise insert the mention.
        editor.registerCommand(
          KEY_ENTER_COMMAND,
          (event: KeyboardEvent | null) => {
            if (
              !event ||
              !isOpenProfileShortcut(event) ||
              highlightedUserId.current === null
            ) {
              return false;
            }
            event.preventDefault();
            openProfile(highlightedUserId.current);
            return true;
          },
          COMMAND_PRIORITY_HIGH
        ),
        // Focus leaving the editor ends the active query. Return false so this
        // stays a passive observer of the blur.
        editor.registerCommand(
          BLUR_COMMAND,
          () => {
            setSuppressedByBlur(true);
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      ),
    [editor, openProfile]
  );

  const checkForMentionMatch = useCallback((text: string) => {
    return checkForAtSignMentions(text, 1);
  }, []);

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeAheadOption>
      onQueryChange={handleQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={users}
      anchorClassName="lexical-mention-anchor"
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        if (
          !anchorElementRef.current ||
          users.length === 0 ||
          suppressedByBlur
        ) {
          highlightedUserId.current = null;
          return null;
        }
        highlightedUserId.current =
          selectedIndex === null ? null : users[selectedIndex]?.id ?? null;

        // The menu is portaled outside the contenteditable, so clicking an
        // option would blur the editor and close the menu before `onClick`
        // fires. Suppressing the mousedown default keeps focus in the editor
        // (mirrors the plain reference-text-input dropdown).
        return createPortal(
          <>
            <MentionListboxAria listboxId={listboxId} />
            <div
              id={listboxId}
              className="lexical-mention-dropdown"
              role="listbox"
              aria-label="User suggestions"
              // Not tab-reachable: the editor keeps focus and drives the menu
              // with arrow keys, so the listbox is only ever focused
              // programmatically.
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
            >
              {users.map((user, i) => (
                <MentionMenuItem
                  key={user.id}
                  id={`typeahead-item-${i}`}
                  data={{
                    id: user.id,
                    name: user.name,
                    avatar: user.avatar,
                    displayAttributes: user.displayAttributes,
                  }}
                  isSelected={selectedIndex === i}
                  onSelect={() => {
                    setHighlightedIndex(i);
                    selectOptionAndCleanUp(user);
                  }}
                  onHover={() => setHighlightedIndex(i)}
                />
              ))}
            </div>
          </>,
          anchorElementRef.current
        );
      }}
    />
  );
};

export default MentionPlugin;
